import type { I18NMessages } from '@atlaskit/intl-messages-provider/types';
import { fetchMessagesForLocale as fetchAgentStudioPermissionSettingsLocale } from '@atlassian/agent-studio-permission-settings/i18n/fetchLocaleMessage';
import { fetchMessagesForLocale as fetchAgentStudioLocale } from '@atlassian/agent-studio/i18n/fetchLocaleMessage';
import { fetchMessagesForLocale as fetchAppBuilderStudioLocale } from '@atlassian/app-builder-studio/i18n/fetchLocaleMessage';
import studioAutomationDefaultMessages from '@atlassian/atlassian-studio-automation/i18n/en';
import { createFetchMessagesForLocale } from '@atlassian/atlassian-studio-common';
import studioUiComponentsDefaultMessages from '@atlassian/atlassian-studio-ui-components/i18n/en';
import { conversationAssistantI18n } from '@atlassian/conversation-assistant/i18n';
import { fetchMessagesForLocale as fetchSolutionTemplatesLocale } from '@atlassian/solution-templates/i18n/fetchLocaleMessage';
import studioCustomSkillsDefaultMessages from '@atlassian/studio-custom-skills/i18n/en';
import { fetchMessagesForLocale as fetchSolutionArchitectUiComponentsLocale } from '@atlassian/studio-solution-architect-ui-components/i18n/fetchLocaleMessage';

import defaultMessages from '../en';

type Catalog = Promise<{ default: I18NMessages }>;

const mergeCatalogs = async (...catalogs: Catalog[]) => ({
	default: Object.assign(
		{},
		...(await Promise.all(catalogs)).map(({ default: messages }) => messages),
	),
});

export const asyncLanguages = {
	cs: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-cs" */ '../cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-cs" */ '@atlassian/atlassian-studio-automation/i18n/cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-cs" */ '@atlassian/atlassian-studio-ui-components/i18n/cs'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-cs" */ '@atlassian/studio-custom-skills/i18n/cs'
			),
		),
	da: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-da" */ '../da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-da" */ '@atlassian/atlassian-studio-automation/i18n/da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-da" */ '@atlassian/atlassian-studio-ui-components/i18n/da'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-da" */ '@atlassian/studio-custom-skills/i18n/da'
			),
		),
	de: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-de" */ '../de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-de" */ '@atlassian/atlassian-studio-automation/i18n/de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-de" */ '@atlassian/atlassian-studio-ui-components/i18n/de'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-de" */ '@atlassian/studio-custom-skills/i18n/de'
			),
		),
	'en-GB': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en_GB" */ '../en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-en_GB" */ '@atlassian/atlassian-studio-automation/i18n/en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-en_GB" */ '@atlassian/atlassian-studio-ui-components/i18n/en_GB'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-en_GB" */ '@atlassian/studio-custom-skills/i18n/en_GB'
			),
		),
	'en-ZZ': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en_ZZ" */ '../en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-en_ZZ" */ '@atlassian/atlassian-studio-automation/i18n/en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-en_ZZ" */ '@atlassian/atlassian-studio-ui-components/i18n/en_ZZ'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-en_ZZ" */ '@atlassian/studio-custom-skills/i18n/en_ZZ'
			),
		),
	en: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en" */ '../en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-en" */ '@atlassian/atlassian-studio-automation/i18n/en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-en" */ '@atlassian/atlassian-studio-ui-components/i18n/en'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-en" */ '@atlassian/studio-custom-skills/i18n/en'
			),
		),
	es: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-es" */ '../es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-es" */ '@atlassian/atlassian-studio-automation/i18n/es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-es" */ '@atlassian/atlassian-studio-ui-components/i18n/es'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-es" */ '@atlassian/studio-custom-skills/i18n/es'
			),
		),
	fi: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-fi" */ '../fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-fi" */ '@atlassian/atlassian-studio-automation/i18n/fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-fi" */ '@atlassian/atlassian-studio-ui-components/i18n/fi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-fi" */ '@atlassian/studio-custom-skills/i18n/fi'
			),
		),
	fr: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-fr" */ '../fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-fr" */ '@atlassian/atlassian-studio-automation/i18n/fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-fr" */ '@atlassian/atlassian-studio-ui-components/i18n/fr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-fr" */ '@atlassian/studio-custom-skills/i18n/fr'
			),
		),
	hu: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-hu" */ '../hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-hu" */ '@atlassian/atlassian-studio-automation/i18n/hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-hu" */ '@atlassian/atlassian-studio-ui-components/i18n/hu'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-hu" */ '@atlassian/studio-custom-skills/i18n/hu'
			),
		),
	it: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-it" */ '../it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-it" */ '@atlassian/atlassian-studio-automation/i18n/it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-it" */ '@atlassian/atlassian-studio-ui-components/i18n/it'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-it" */ '@atlassian/studio-custom-skills/i18n/it'
			),
		),
	ja: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ja" */ '../ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-ja" */ '@atlassian/atlassian-studio-automation/i18n/ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-ja" */ '@atlassian/atlassian-studio-ui-components/i18n/ja'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-ja" */ '@atlassian/studio-custom-skills/i18n/ja'
			),
		),
	ko: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ko" */ '../ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-ko" */ '@atlassian/atlassian-studio-automation/i18n/ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-ko" */ '@atlassian/atlassian-studio-ui-components/i18n/ko'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-ko" */ '@atlassian/studio-custom-skills/i18n/ko'
			),
		),
	nb: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-nb" */ '../nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-nb" */ '@atlassian/atlassian-studio-automation/i18n/nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-nb" */ '@atlassian/atlassian-studio-ui-components/i18n/nb'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-nb" */ '@atlassian/studio-custom-skills/i18n/nb'
			),
		),
	nl: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-nl" */ '../nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-nl" */ '@atlassian/atlassian-studio-automation/i18n/nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-nl" */ '@atlassian/atlassian-studio-ui-components/i18n/nl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-nl" */ '@atlassian/studio-custom-skills/i18n/nl'
			),
		),
	pl: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-pl" */ '../pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-pl" */ '@atlassian/atlassian-studio-automation/i18n/pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-pl" */ '@atlassian/atlassian-studio-ui-components/i18n/pl'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-pl" */ '@atlassian/studio-custom-skills/i18n/pl'
			),
		),
	'pt-BR': (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-pt_BR" */ '../pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-pt_BR" */ '@atlassian/atlassian-studio-automation/i18n/pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-pt_BR" */ '@atlassian/atlassian-studio-ui-components/i18n/pt_BR'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-pt_BR" */ '@atlassian/studio-custom-skills/i18n/pt_BR'
			),
		),
	ru: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ru" */ '../ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-ru" */ '@atlassian/atlassian-studio-automation/i18n/ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-ru" */ '@atlassian/atlassian-studio-ui-components/i18n/ru'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-ru" */ '@atlassian/studio-custom-skills/i18n/ru'
			),
		),
	sv: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-sv" */ '../sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-sv" */ '@atlassian/atlassian-studio-automation/i18n/sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-sv" */ '@atlassian/atlassian-studio-ui-components/i18n/sv'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-sv" */ '@atlassian/studio-custom-skills/i18n/sv'
			),
		),
	th: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-th" */ '../th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-th" */ '@atlassian/atlassian-studio-automation/i18n/th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-th" */ '@atlassian/atlassian-studio-ui-components/i18n/th'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-th" */ '@atlassian/studio-custom-skills/i18n/th'
			),
		),
	tr: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-tr" */ '../tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-tr" */ '@atlassian/atlassian-studio-automation/i18n/tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-tr" */ '@atlassian/atlassian-studio-ui-components/i18n/tr'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-tr" */ '@atlassian/studio-custom-skills/i18n/tr'
			),
		),
	uk: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-uk" */ '../uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-uk" */ '@atlassian/atlassian-studio-automation/i18n/uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-uk" */ '@atlassian/atlassian-studio-ui-components/i18n/uk'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-uk" */ '@atlassian/studio-custom-skills/i18n/uk'
			),
		),
	vi: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-vi" */ '../vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-vi" */ '@atlassian/atlassian-studio-automation/i18n/vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-vi" */ '@atlassian/atlassian-studio-ui-components/i18n/vi'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-vi" */ '@atlassian/studio-custom-skills/i18n/vi'
			),
		),
	zh_TW: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-zh_TW" */ '../zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-zh_TW" */ '@atlassian/atlassian-studio-automation/i18n/zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-zh_TW" */ '@atlassian/atlassian-studio-ui-components/i18n/zh_TW'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-zh_TW" */ '@atlassian/studio-custom-skills/i18n/zh_TW'
			),
		),
	zh: (): Catalog =>
		mergeCatalogs(
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-zh" */ '../zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-automation-i18n-zh" */ '@atlassian/atlassian-studio-automation/i18n/zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/atlassian-studio-ui-components-i18n-zh" */ '@atlassian/atlassian-studio-ui-components/i18n/zh'
			),
			import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-custom-skills-i18n-zh" */ '@atlassian/studio-custom-skills/i18n/zh'
			),
		),
} as const;

const studioFoundationsDefaultMessages: I18NMessages = {
	...defaultMessages,
	...studioAutomationDefaultMessages,
	...studioUiComponentsDefaultMessages,
	...studioCustomSkillsDefaultMessages,
};

const sitePickerAsyncLanguages = {
	cs: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-cs" */ '@atlassian/atlassian-studio-site-picker/i18n/cs'
		),
	da: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-da" */ '@atlassian/atlassian-studio-site-picker/i18n/da'
		),
	de: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-de" */ '@atlassian/atlassian-studio-site-picker/i18n/de'
		),
	'en-GB': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en_GB" */ '@atlassian/atlassian-studio-site-picker/i18n/en_GB'
		),
	'en-ZZ': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en_ZZ" */ '@atlassian/atlassian-studio-site-picker/i18n/en_ZZ'
		),
	en: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en" */ '@atlassian/atlassian-studio-site-picker/i18n/en'
		),
	es: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-es" */ '@atlassian/atlassian-studio-site-picker/i18n/es'
		),
	fi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-fi" */ '@atlassian/atlassian-studio-site-picker/i18n/fi'
		),
	fr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-fr" */ '@atlassian/atlassian-studio-site-picker/i18n/fr'
		),
	hu: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-hu" */ '@atlassian/atlassian-studio-site-picker/i18n/hu'
		),
	it: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-it" */ '@atlassian/atlassian-studio-site-picker/i18n/it'
		),
	ja: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ja" */ '@atlassian/atlassian-studio-site-picker/i18n/ja'
		),
	ko: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ko" */ '@atlassian/atlassian-studio-site-picker/i18n/ko'
		),
	nb: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-nb" */ '@atlassian/atlassian-studio-site-picker/i18n/nb'
		),
	nl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-nl" */ '@atlassian/atlassian-studio-site-picker/i18n/nl'
		),
	pl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-pl" */ '@atlassian/atlassian-studio-site-picker/i18n/pl'
		),
	'pt-BR': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-pt_BR" */ '@atlassian/atlassian-studio-site-picker/i18n/pt_BR'
		),
	ru: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ru" */ '@atlassian/atlassian-studio-site-picker/i18n/ru'
		),
	sv: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-sv" */ '@atlassian/atlassian-studio-site-picker/i18n/sv'
		),
	th: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-th" */ '@atlassian/atlassian-studio-site-picker/i18n/th'
		),
	tr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-tr" */ '@atlassian/atlassian-studio-site-picker/i18n/tr'
		),
	uk: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-uk" */ '@atlassian/atlassian-studio-site-picker/i18n/uk'
		),
	vi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-vi" */ '@atlassian/atlassian-studio-site-picker/i18n/vi'
		),
	zh_TW: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-zh_TW" */ '@atlassian/atlassian-studio-site-picker/i18n/zh_TW'
		),
	zh: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-zh" */ '@atlassian/atlassian-studio-site-picker/i18n/zh'
		),
} as const;

const globalSideNavAsyncLanguages = {
	cs: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-cs" */ '@atlassian/agent-global-side-navigation/i18n/cs'
		),
	da: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-da" */ '@atlassian/agent-global-side-navigation/i18n/da'
		),
	de: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-de" */ '@atlassian/agent-global-side-navigation/i18n/de'
		),
	'en-GB': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en_GB" */ '@atlassian/agent-global-side-navigation/i18n/en_GB'
		),
	'en-ZZ': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en_ZZ" */ '@atlassian/agent-global-side-navigation/i18n/en_ZZ'
		),
	en: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en" */ '@atlassian/agent-global-side-navigation/i18n/en'
		),
	es: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-es" */ '@atlassian/agent-global-side-navigation/i18n/es'
		),
	fi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-fi" */ '@atlassian/agent-global-side-navigation/i18n/fi'
		),
	fr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-fr" */ '@atlassian/agent-global-side-navigation/i18n/fr'
		),
	hu: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-hu" */ '@atlassian/agent-global-side-navigation/i18n/hu'
		),
	it: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-it" */ '@atlassian/agent-global-side-navigation/i18n/it'
		),
	ja: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ja" */ '@atlassian/agent-global-side-navigation/i18n/ja'
		),
	ko: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ko" */ '@atlassian/agent-global-side-navigation/i18n/ko'
		),
	nb: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-nb" */ '@atlassian/agent-global-side-navigation/i18n/nb'
		),
	nl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-nl" */ '@atlassian/agent-global-side-navigation/i18n/nl'
		),
	pl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-pl" */ '@atlassian/agent-global-side-navigation/i18n/pl'
		),
	'pt-BR': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-pt_BR" */ '@atlassian/agent-global-side-navigation/i18n/pt_BR'
		),
	ru: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ru" */ '@atlassian/agent-global-side-navigation/i18n/ru'
		),
	sv: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-sv" */ '@atlassian/agent-global-side-navigation/i18n/sv'
		),
	th: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-th" */ '@atlassian/agent-global-side-navigation/i18n/th'
		),
	tr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-tr" */ '@atlassian/agent-global-side-navigation/i18n/tr'
		),
	uk: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-uk" */ '@atlassian/agent-global-side-navigation/i18n/uk'
		),
	vi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-vi" */ '@atlassian/agent-global-side-navigation/i18n/vi'
		),
	'zh-TW': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-zh_TW" */ '@atlassian/agent-global-side-navigation/i18n/zh_TW'
		),
	zh: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-zh" */ '@atlassian/agent-global-side-navigation/i18n/zh'
		),
} as const;

const fetchStudioFoundationsMessages = createFetchMessagesForLocale(
	asyncLanguages,
	studioFoundationsDefaultMessages,
);
const fetchSitePickerMessages = createFetchMessagesForLocale(sitePickerAsyncLanguages, {});
const fetchGlobalSideNavMessages = createFetchMessagesForLocale(globalSideNavAsyncLanguages, {});
const fetchConversationAssistantMessages = createFetchMessagesForLocale(
	conversationAssistantI18n.asyncLanguages,
	conversationAssistantI18n.defaultMessages,
);

export const fetchMessagesForLocale = async (locale: string): Promise<I18NMessages> => {
	const [
		studioFoundations,
		sitePicker,
		globalSideNav,
		conversationAssistant,
		appBuilderStudio,
		agentStudio,
		agentStudioPermissionSettings,
		solutionArchitectUiComponents,
		solutionTemplates,
	] = await Promise.allSettled([
		fetchStudioFoundationsMessages(locale),
		fetchSitePickerMessages(locale),
		fetchGlobalSideNavMessages(locale),
		fetchConversationAssistantMessages(locale),
		fetchAppBuilderStudioLocale(locale),
		fetchAgentStudioLocale(locale),
		fetchAgentStudioPermissionSettingsLocale(locale),
		fetchSolutionArchitectUiComponentsLocale(locale),
		fetchSolutionTemplatesLocale(locale),
	]);

	return Object.assign(
		{},
		studioFoundations.status === 'fulfilled'
			? studioFoundations.value
			: studioFoundationsDefaultMessages,
		sitePicker.status === 'fulfilled' ? sitePicker.value : {},
		globalSideNav.status === 'fulfilled' ? globalSideNav.value : {},
		conversationAssistant.status === 'fulfilled'
			? conversationAssistant.value
			: conversationAssistantI18n.defaultMessages,
		appBuilderStudio.status === 'fulfilled' ? appBuilderStudio.value : {},
		agentStudio.status === 'fulfilled' ? agentStudio.value : {},
		agentStudioPermissionSettings.status === 'fulfilled' ? agentStudioPermissionSettings.value : {},
		solutionArchitectUiComponents.status === 'fulfilled' ? solutionArchitectUiComponents.value : {},
		solutionTemplates.status === 'fulfilled' ? solutionTemplates.value : {},
	) as I18NMessages;
};

export { defaultMessages };
